import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PanelShell, PlnBarra, PlnBarraRot, PlnKpis, PlnKpi, PlnBloco, stickyTh } from './PanelShell'
import { NeuSwitch } from './NeuSwitch'
import { FiLoader, FiRefreshCw, FiSearch, FiX, FiCheck, FiAlertTriangle, FiInfo, FiTrash2, FiTarget } from 'react-icons/fi'

// Painel VANGUARD (06/10/2026, pedido do diretor) — o controle da aba Vanguard
// do app.twms. Quatro partes:
//   Liberação  quem vê: chave geral, cargos, distritos, testadores, bloqueados
//   Tela       o que a aba mostra, o nome dela e o texto pronto do WhatsApp
//   Janelas    a janela de pagamento de cada vitrine (o Draco só guarda a do
//              último relatório que cada usuário rodou — aqui ela fica)
//   Acessos    quem abriu, quando, que grupo olhou, quem chamou no WhatsApp
// Tudo mora no app-twms; o fin repassa (routes/vanguard.cjs). Só admin.

type Papel = 'lider' | 'empresario' | 'distribuicao'
type Mostrar = 'andamento' | 'faixas' | 'semanas' | 'historico' | 'bonus' | 'recrutas' | 'whatsapp'
interface Config {
  ativo: boolean
  papeis: Record<Papel, boolean>
  distritos: string[]
  testadores: string[]
  bloqueados: string[]
  mostrar: Record<Mostrar, boolean>
  textos: { aba: string; mensagem: string }
  por?: string | null
  atualizado_em?: string | null
}
interface Pessoa { codigo: string; nome: string | null; grupo: string | null; distrito: string | null; papel: string | null }
interface Janela { periodo: number; inicio: string; fim: string; por: string | null; atualizado_em: string }
interface Draco {
  semana_atual: number; periodo_atual: number
  periodos: { periodo: number; semanas: [number, number]; inicio: string; fim: string; sexta_da_quarta: string }[]
  draco: { periodo: number; usuario: string | null; inicio: string; fim: string }[]
}
interface Dados { config: Config; pessoas: Record<string, Pessoa>; distritos: { codigo: string; nome: string; grupos: number }[]; janelas: Janela[]; draco: Draco | null }
interface Acessos {
  dias: number
  resumo: { liberados: number; abriram: number; aberturas: number; whatsapp: number }
  pessoas: (Pessoa & { aberturas: number; grupos_trocados: number; whatsapp: number; dias: number; grupos_vistos: number; primeira: string; ultima: string })[]
  por_dia: { dia: string; pessoas: number; aberturas: number; whatsapp: number }[]
  chamadas: { em: string; quem: Pessoa; para: Pessoa | null; grupo: string | null; vitrine: number | null }[]
  nao_abriram: Pessoa[]
}

const PAPEIS: { k: Papel; rot: string; dica: string }[] = [
  { k: 'lider', rot: 'Líderes', dica: 'Cada líder vê só o próprio grupo' },
  { k: 'empresario', rot: 'Empresárias', dica: 'Veem os grupos do distrito delas' },
  { k: 'distribuicao', rot: 'Distribuição', dica: 'Vê todos os grupos, com seletor' },
]
const MOSTRAR: { k: Mostrar; rot: string; dica: string }[] = [
  { k: 'andamento', rot: 'Vitrine em andamento', dica: 'A previsão da vitrine atual, que muda todo dia. Desligado, só aparece a vitrine fechada.' },
  { k: 'bonus', rot: 'Bônus em reais', dica: 'Faixa + 3% + distrito = o que a Tupperware pagou (ou o previsto).' },
  { k: 'recrutas', rot: 'Recrutas', dica: 'A linha "Recrutas na vitrine" no topo.' },
  { k: 'semanas', rot: 'Semana a semana', dica: 'Venda e recrutas de cada semana da vitrine.' },
  { k: 'faixas', rot: 'Tabela de faixas', dica: 'As faixas do mês com o objetivo e o bônus de cada uma.' },
  { k: 'historico', rot: 'Últimas vitrines', dica: 'A faixa e o bônus das 12 vitrines anteriores.' },
  { k: 'whatsapp', rot: 'Botão do WhatsApp', dica: '"Conversar no WhatsApp" em cada documento em aberto, com o texto pronto abaixo.' },
]
const PAPEL_NOME: Record<string, string> = { lider: 'Líder', empresario: 'Empresária', distribuicao: 'Distribuição', consultora: 'Consultora' }
const num = (n: number) => n.toLocaleString('pt-BR')
const dia = (iso?: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '')
const dataHora = (s?: string | null) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} ${s.slice(11, 16)}` : '')
const vit = (p: number) => `Vitrine ${p % 100}/${String(Math.floor(p / 100)).slice(2)}`
const titulo = (s?: string | null) => (s ? s.toLowerCase().replace(/(^|[\s/-])(\S)/g, (_, a, b) => a + b.toUpperCase()) : '')
const igual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

type Parte = 'liberacao' | 'tela' | 'janelas' | 'acessos'
const PARTES: { k: Parte; rot: string }[] = [
  { k: 'liberacao', rot: 'Liberação' }, { k: 'tela', rot: 'Tela' }, { k: 'janelas', rot: 'Janelas de pagamento' }, { k: 'acessos', rot: 'Acessos' },
]

export function VanguardPanel() {
  const { authenticatedFetch } = useAuth()
  const [parte, setParte] = useState<Parte>(() => { try { return (localStorage.getItem('fin.vanguard.parte') as Parte) || 'liberacao' } catch { return 'liberacao' } })
  const [dados, setDados] = useState<Dados | null>(null)
  const [cfg, setCfg] = useState<Config | null>(null)
  const [pessoas, setPessoas] = useState<Record<string, Pessoa>>({})
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const api = useCallback(async (url: string, init?: RequestInit) => {
    const r = await authenticatedFetch(url, init)
    const d = await r.json().catch(() => ({}))
    if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`)
    return d
  }, [authenticatedFetch])

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const d: Dados = await api('/api/vanguard/config')
      setDados(d); setCfg(d.config); setPessoas(d.pessoas || {})
    } catch (e) { setErro((e as Error).message) } finally { setCarregando(false) }
  }, [api])
  useEffect(() => { carregar() }, [carregar])
  const irPara = (p: Parte) => { setParte(p); try { localStorage.setItem('fin.vanguard.parte', p) } catch { /* */ } }

  const sujo = !!(cfg && dados && !igual(
    { ...cfg, por: undefined, atualizado_em: undefined }, { ...dados.config, por: undefined, atualizado_em: undefined }))
  const salvar = async () => {
    if (!cfg) return
    setSalvando(true); setErro(null); setOk(null)
    try {
      const r = await api('/api/vanguard/config', { method: 'PUT', body: JSON.stringify({ config: cfg }) })
      setDados(d => (d ? { ...d, config: r.config } : d)); setCfg(r.config)
      setOk('Salvo. O app passa a seguir em até 30 segundos.')
    } catch (e) { setErro((e as Error).message) } finally { setSalvando(false) }
  }

  const resumo = useMemo(() => {
    if (!cfg) return ''
    const t = cfg.testadores.length
    if (!cfg.ativo) return t ? `Desligada — só ${t === 1 ? '1 testador' : `${t} testadores`} vê` : 'Desligada para todos'
    const ps = PAPEIS.filter(p => cfg.papeis[p.k]).map(p => p.rot.toLowerCase())
    const ds = cfg.distritos.length ? ` de ${cfg.distritos.length === 1 ? '1 distrito' : `${cfg.distritos.length} distritos`}` : ''
    return ps.length ? `Ligada para ${ps.join(', ')}${ds}` : 'Ligada, mas nenhum cargo escolhido'
  }, [cfg])

  // PLANOS (21/10/2026): o título "Vanguard no app" foi pro cabeçalho do
  // PanelShell; Desfazer/Salvar/Atualizar viraram `acoes`; as abas e o estado
  // da liberação (bolinha + resumo) moram numa PlnBarra.
  const acoes = (
    <>
        {sujo && <button onClick={() => dados && setCfg(dados.config)} className="neu-btn px-2.5 py-1.5 rounded-lg border border-border text-sm">Desfazer</button>}
        {(parte === 'liberacao' || parte === 'tela') && (
          <button onClick={salvar} disabled={!sujo || salvando}
            className="neu-btn px-3 py-1.5 rounded-lg border border-primary bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {salvando ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />} Salvar
          </button>
        )}
        <button onClick={carregar} disabled={carregando} title="Atualizar" className="neu-btn p-2 rounded-lg border border-border disabled:opacity-60">
          <FiRefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
        </button>
    </>
  )

  const toolbar = (
    <div className="flex flex-col gap-3">
      <PlnBarra>
        <PlnBarraRot>Seção</PlnBarraRot>
      <div className="flex flex-wrap gap-1.5" role="tablist">
        {PARTES.map(p => (
          <button key={p.k} role="tab" aria-selected={parte === p.k} onClick={() => irPara(p.k)}
            className={`neu-btn px-3 py-1.5 rounded-lg border text-sm ${parte === p.k ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border'}`}>
            {p.rot}{(p.k === 'liberacao' || p.k === 'tela') && sujo && parte !== p.k ? ' •' : ''}
          </button>
        ))}
      </div>
        <span className="ml-auto text-sm text-muted-foreground flex items-center gap-1.5 min-w-0">
          <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${cfg?.ativo ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {resumo}{dados?.config.por ? ` · última mudança: ${dados.config.por}${dados.config.atualizado_em ? `, ${dataHora(String(dados.config.atualizado_em).replace('T', ' '))}` : ''}` : ''}
        </span>
      </PlnBarra>
      {/* PLANOS (21/10, subtelas): aviso de erro/salvo numa caixa, não texto solto */}
      {erro && <p className="w-full rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-400 flex items-center gap-1.5"><FiAlertTriangle className="w-4 h-4 shrink-0" /> {erro}</p>}
      {ok && !sujo && <p className="w-full rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3.5 py-2.5 text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5"><FiCheck className="w-4 h-4 shrink-0" /> {ok}</p>}
    </div>
  )

  return (
    <PanelShell titulo="Vanguard no app" sub="Quem vê a aba Vanguard no app.twms, como ela aparece e quem já abriu"
      icone={<FiTarget />} acoes={acoes} toolbar={toolbar}>
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {!cfg || !dados ? (
          <p className="rounded-xl border border-border bg-card px-4 py-3.5 text-sm text-muted-foreground flex items-center gap-2">{carregando ? <><FiLoader className="w-4 h-4 animate-spin" /> Lendo a configuração…</> : 'Sem dados.'}</p>
        ) : parte === 'liberacao' ? (
          <Liberacao cfg={cfg} setCfg={setCfg} dados={dados} pessoas={pessoas} setPessoas={setPessoas} api={api} />
        ) : parte === 'tela' ? (
          <Tela cfg={cfg} setCfg={setCfg} />
        ) : parte === 'janelas' ? (
          <Janelas dados={dados} api={api} aoMudar={js => setDados(d => (d ? { ...d, janelas: js } : d))} />
        ) : (
          <AcessosParte api={api} />
        )}
      </div>
    </PanelShell>
  )
}

// PLANOS (21/10/2026, subtelas): o cartão de cada parte virou PlnBloco — título 16
// no cabeçalho do papel, a dica vira a nota (13px), corpo com respiro. Sem mb-*:
// o espaço vem do gap da pilha (PILHA).
// `solto`: o .pln-bloco corta o que vaza (overflow hidden); a lista de pessoas
// tem um menu de busca absoluto que precisa sair do papel.
const Cartao = ({ titulo: t, dica, solto, children }: { titulo: string; dica?: string; solto?: boolean; children: ReactNode }) => (
  <PlnBloco titulo={t} nota={dica} style={solto ? { overflow: 'visible' } : undefined}>
    <div className="p-5">{children}</div>
  </PlnBloco>
)
// Em tela larga os cartões vão em DUAS colunas (21/10/2026): numa coluna de
// 980px a metade direita da tela ficava vazia.
const PILHA = 'grid gap-4 pb-1 items-start grid-cols-1 2xl:grid-cols-2'

function Liberacao({ cfg, setCfg, dados, pessoas, setPessoas, api }: {
  cfg: Config; setCfg: (c: Config) => void; dados: Dados; pessoas: Record<string, Pessoa>
  setPessoas: (f: (p: Record<string, Pessoa>) => Record<string, Pessoa>) => void; api: (u: string, i?: RequestInit) => Promise<any>
}) {
  const mudar = (p: Partial<Config>) => setCfg({ ...cfg, ...p })
  const alternaDistrito = (c: string) => mudar({ distritos: cfg.distritos.includes(c) ? cfg.distritos.filter(x => x !== c) : [...cfg.distritos, c] })
  return (
    <div className={PILHA}>
      <Cartao titulo="A aba Vanguard no app" dica="Desligada, ninguém vê a aba — só os testadores abaixo. Ligada, vale o que está escolhido nos cargos e distritos.">
        <label className="flex items-center gap-3 cursor-pointer w-fit">
          <NeuSwitch on={cfg.ativo} onClick={() => mudar({ ativo: !cfg.ativo })} />
          <span className="text-sm font-medium">{cfg.ativo ? 'Ligada' : 'Desligada'}</span>
        </label>
      </Cartao>

      <Cartao titulo="Cargos" dica="Consultora não vê: a Vanguard é do grupo.">
        <div className="flex flex-wrap gap-2">
          {PAPEIS.map(p => (
            <label key={p.k} className={`neu-btn flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer ${cfg.papeis[p.k] ? 'border-primary bg-primary/5' : 'border-border'} ${!cfg.ativo ? 'opacity-60' : ''}`}>
              <input type="checkbox" className="mt-0.5" checked={cfg.papeis[p.k]} onChange={() => mudar({ papeis: { ...cfg.papeis, [p.k]: !cfg.papeis[p.k] } })} />
              <span><span className="block text-sm font-medium">{p.rot}</span><span className="block text-[12.5px] text-muted-foreground mt-0.5">{p.dica}</span></span>
            </label>
          ))}
        </div>
      </Cartao>

      <Cartao titulo="Distritos" dica="Nenhum marcado = todos. Marcando, só líderes e empresárias desses distritos veem (a distribuição sempre vê).">
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => mudar({ distritos: [] })}
            className={`neu-btn px-3 py-1.5 rounded-lg border text-sm ${!cfg.distritos.length ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border'}`}>Todos</button>
          {dados.distritos.map(d => (
            <button key={d.codigo} onClick={() => alternaDistrito(d.codigo)} title={`${d.grupos} grupos`}
              className={`neu-btn px-3 py-1.5 rounded-lg border text-sm ${cfg.distritos.includes(d.codigo) ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border'}`}>
              {titulo(d.nome)} <span className="text-xs text-muted-foreground tabular-nums">{d.grupos}</span>
            </button>
          ))}
        </div>
      </Cartao>

      <ListaPessoas titulo="Testadores" dica="Veem a aba mesmo com ela desligada — para conferir antes de liberar."
        codigos={cfg.testadores} pessoas={pessoas} setPessoas={setPessoas} api={api}
        aoMudar={l => mudar({ testadores: l, bloqueados: cfg.bloqueados.filter(c => !l.includes(c)) })} />
      <ListaPessoas titulo="Bloqueados" dica="Nunca veem a aba, mesmo com o cargo liberado."
        codigos={cfg.bloqueados} pessoas={pessoas} setPessoas={setPessoas} api={api}
        aoMudar={l => mudar({ bloqueados: l, testadores: cfg.testadores.filter(c => !l.includes(c)) })} />
    </div>
  )
}

function ListaPessoas({ titulo: t, dica, codigos, pessoas, setPessoas, api, aoMudar }: {
  titulo: string; dica: string; codigos: string[]; pessoas: Record<string, Pessoa>
  setPessoas: (f: (p: Record<string, Pessoa>) => Record<string, Pessoa>) => void; api: (u: string) => Promise<any>; aoMudar: (l: string[]) => void
}) {
  const [busca, setBusca] = useState('')
  const [achados, setAchados] = useState<Pessoa[]>([])
  useEffect(() => {
    if (busca.trim().length < 2) { setAchados([]); return }
    const t = setTimeout(() => {
      api(`/api/vanguard/pessoas?busca=${encodeURIComponent(busca.trim())}`).then(d => setAchados(d.pessoas || [])).catch(() => setAchados([]))
    }, 300)
    return () => clearTimeout(t)
  }, [busca, api])
  return (
    <Cartao titulo={t} dica={dica} solto>
      {codigos.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-4">
          {codigos.map(c => {
            const p = pessoas[c]
            return (
              <li key={c} className="neu-tag flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg border border-border bg-secondary/40 text-sm">
                <span>{p?.nome ? titulo(p.nome) : c}</span>
                <span className="text-xs text-muted-foreground">{p?.papel ? PAPEL_NOME[p.papel] : ''}{p?.grupo ? ` · ${titulo(p.grupo)}` : ''}</span>
                <button onClick={() => aoMudar(codigos.filter(x => x !== c))} title="Tirar" className="p-0.5 rounded hover:bg-secondary"><FiX className="w-3.5 h-3.5" /></button>
              </li>
            )
          })}
        </ul>
      )}
      <div className="relative w-full max-w-[420px]">
        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Acrescentar: nome ou código"
          className="neu-input h-9 pl-8 pr-3 rounded-lg bg-card border border-border text-sm w-full" />
        {achados.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
            {achados.map(p => (
              <li key={p.codigo}>
                <button disabled={codigos.includes(p.codigo)} onClick={() => { setPessoas(m => ({ ...m, [p.codigo]: p })); aoMudar([...codigos, p.codigo]); setBusca('') }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 disabled:opacity-50">
                  <span className="font-medium">{titulo(p.nome)}</span> <span className="text-xs text-muted-foreground">{p.codigo} · {PAPEL_NOME[p.papel || ''] || ''}{p.grupo ? ` · ${titulo(p.grupo)}` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Cartao>
  )
}

function Tela({ cfg, setCfg }: { cfg: Config; setCfg: (c: Config) => void }) {
  const exemplo = (cfg.textos.mensagem || '')
    .split('{nome}').join('Maria').split('{valor}').join('R$ 449,76').split('{vencimento}').join('02/09').split('{vitrine}').join('9')
  return (
    <div className={PILHA}>
      <Cartao titulo="O que a aba mostra" dica="A conta da Vanguard, a faixa e os documentos em aberto e pagos aparecem sempre.">
        <div className="grid gap-2 sm:grid-cols-2">
          {MOSTRAR.map(m => (
            <label key={m.k} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-secondary/40 cursor-pointer">
              <span className="pt-0.5"><NeuSwitch on={cfg.mostrar[m.k]} onClick={() => setCfg({ ...cfg, mostrar: { ...cfg.mostrar, [m.k]: !cfg.mostrar[m.k] } })} /></span>
              <span><span className="block text-sm font-medium">{m.rot}</span><span className="block text-[12.5px] leading-snug text-muted-foreground mt-0.5">{m.dica}</span></span>
            </label>
          ))}
        </div>
      </Cartao>
      <Cartao titulo="Nome da aba" dica="Aparece na barra de baixo do app (até 14 letras, para caber no celular).">
        <input value={cfg.textos.aba} maxLength={14} onChange={e => setCfg({ ...cfg, textos: { ...cfg.textos, aba: e.target.value } })}
          className="neu-input h-9 px-3 rounded-lg bg-card border border-border text-sm w-56" />
      </Cartao>
      <Cartao titulo="Texto pronto do WhatsApp" dica="Abre escrito quando a líder toca em &quot;Conversar no WhatsApp&quot; num documento em aberto. Ela pode mudar antes de mandar.">
        <textarea value={cfg.textos.mensagem} maxLength={600} rows={4} onChange={e => setCfg({ ...cfg, textos: { ...cfg.textos, mensagem: e.target.value } })}
          className="neu-input w-full px-3 py-2.5 rounded-lg bg-card border border-border text-sm" />
        <p className="text-[12.5px] text-muted-foreground mt-1.5">Troca sozinho: <code>{'{nome}'}</code> primeiro nome · <code>{'{valor}'}</code> quanto deve · <code>{'{vencimento}'}</code> data · <code>{'{vitrine}'}</code> número da vitrine</p>
        <p className="text-[13px] font-semibold text-foreground mt-5 mb-2">Como chega</p>
        <div className="rounded-xl bg-[#e7f7e1] dark:bg-emerald-950/40 border border-border px-3.5 py-2.5 max-w-[340px] text-sm text-[#12211f] dark:text-foreground whitespace-pre-wrap">{exemplo || <span className="text-muted-foreground">sem texto: a conversa abre vazia</span>}</div>
      </Cartao>
    </div>
  )
}

function Janelas({ dados, api, aoMudar }: { dados: Dados; api: (u: string, i?: RequestInit) => Promise<any>; aoMudar: (j: Janela[]) => void }) {
  const [editando, setEditando] = useState<Record<number, { inicio: string; fim: string }>>({})
  const [falha, setFalha] = useState<string | null>(null)
  const [indo, setIndo] = useState<number | null>(null)
  if (!dados.draco) return <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-400">Não consegui ler as vitrines no Draco agora. Tente atualizar.</p>
  const confirmadas = new Map(dados.janelas.map(j => [Number(j.periodo), j]))
  const sugestoes = new Map<number, Draco['draco']>()
  for (const s of dados.draco.draco) sugestoes.set(s.periodo, [...(sugestoes.get(s.periodo) || []), s])
  const salvar = async (p: number, j: { inicio: string; fim: string }) => {
    setIndo(p); setFalha(null)
    try { const r = await api(`/api/vanguard/janela/${p}`, { method: 'PUT', body: JSON.stringify(j) }); aoMudar(r.janelas); setEditando(e => { const n = { ...e }; delete n[p]; return n }) }
    catch (e) { setFalha((e as Error).message) } finally { setIndo(null) }
  }
  const apagar = async (p: number) => {
    setIndo(p); setFalha(null)
    try { const r = await api(`/api/vanguard/janela/${p}`, { method: 'DELETE' }); aoMudar(r.janelas) }
    catch (e) { setFalha((e as Error).message) } finally { setIndo(null) }
  }
  // PLANOS (21/10/2026, subtelas): explicação numa caixa, tabela num PlnBloco (nota = contagem)
  return (
    <div className="max-w-[1100px] flex flex-col gap-4 pb-1">
      <div className="px-4 py-3 rounded-xl border border-primary/30 bg-primary/5 text-sm leading-relaxed flex gap-2.5">
        <FiInfo className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
        <span>A janela é o período em que os documentos da vitrine anterior, pagos com atraso, <b>voltam</b> para a conta da Vanguard — a "data inicial/final" do relatório Vanguard do Draco.
          O Draco só guarda a do <b>último</b> relatório de cada usuário; confirmando aqui, ela fica. Sem janela confirmada, o app usa a do Draco, depois começa no dia seguinte ao fim da anterior e vai até hoje.</span>
      </div>
      {falha && <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-400">{falha}</p>}
      <PlnBloco titulo="Janelas por vitrine" nota={`${dados.draco.periodos.length} vitrines · ${dados.janelas.length} confirmada(s)`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground">
            <th className={`${stickyTh} px-3 py-2.5`}>Vitrine</th><th className={`${stickyTh} px-3 py-2.5`}>Semanas</th>
            <th className={`${stickyTh} px-3 py-2.5`}>No Draco</th><th className={`${stickyTh} px-3 py-2.5`}>Janela que vale</th><th className={`${stickyTh} px-3 py-2.5`} />
          </tr></thead>
          <tbody>
            {dados.draco.periodos.map(p => {
              const c = confirmadas.get(p.periodo)
              const sug = sugestoes.get(p.periodo) || []
              const ed = editando[p.periodo]
              const atual = p.periodo === dados.draco!.periodo_atual
              return (
                <tr key={p.periodo} className="neu-tr border-t border-border align-top">
                  <td className="px-3 py-2 whitespace-nowrap"><b>{vit(p.periodo)}</b>{atual && <span className="ml-1.5 text-xs text-primary">acontecendo</span>}
                    <div className="text-xs text-muted-foreground">{dia(p.inicio)} a {dia(p.fim)}</div></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap">{p.semanas[0] % 100}–{p.semanas[1] % 100}<div>sexta da 4ª: {dia(p.sexta_da_quarta)}</div></td>
                  <td className="px-3 py-2 text-xs">
                    {sug.length ? sug.map((s, i) => (
                      <div key={i} className="whitespace-nowrap">{dia(s.inicio)} a {dia(s.fim)} <span className="text-muted-foreground">· {titulo(s.usuario)}</span>
                        {!c && !ed && <button onClick={() => salvar(p.periodo, { inicio: s.inicio, fim: s.fim })} disabled={indo === p.periodo} className="ml-2 underline underline-offset-2 text-primary">confirmar</button>}</div>
                    )) : <span className="text-muted-foreground">nada gravado</span>}
                  </td>
                  <td className="px-3 py-2">
                    {ed ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <input type="date" value={ed.inicio} onChange={e => setEditando(x => ({ ...x, [p.periodo]: { ...ed, inicio: e.target.value } }))} className="neu-input px-2 py-1 rounded-lg bg-card border border-border text-sm" />
                        <span className="text-xs">a</span>
                        <input type="date" value={ed.fim} onChange={e => setEditando(x => ({ ...x, [p.periodo]: { ...ed, fim: e.target.value } }))} className="neu-input px-2 py-1 rounded-lg bg-card border border-border text-sm" />
                        <button onClick={() => salvar(p.periodo, ed)} disabled={indo === p.periodo || !ed.inicio || !ed.fim} className="neu-btn px-3 py-1.5 rounded-lg border border-primary bg-primary text-primary-foreground text-[13px] disabled:opacity-50">Salvar</button>
                        <button onClick={() => setEditando(x => { const n = { ...x }; delete n[p.periodo]; return n })} className="text-[13px] underline underline-offset-2">cancelar</button>
                      </div>
                    ) : c ? (
                      <div><span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-medium"><FiCheck className="w-3.5 h-3.5" /> {dia(c.inicio)} a {dia(c.fim)}</span>
                        <div className="text-xs text-muted-foreground">confirmada{c.por ? ` · ${c.por}` : ''}</div></div>
                    ) : <span className="text-xs text-muted-foreground">{sug.length ? 'a do Draco (não confirmada)' : 'regra automática (dia seguinte ao fim da anterior → hoje)'}</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!ed && <button onClick={() => setEditando(x => ({ ...x, [p.periodo]: { inicio: c?.inicio || sug[0]?.inicio || p.sexta_da_quarta, fim: c?.fim || sug[0]?.fim || '' } }))} className="neu-btn px-3 py-1.5 rounded-lg border border-border text-[13px]">{c ? 'Mudar' : 'Definir'}</button>}
                    {c && !ed && <button onClick={() => apagar(p.periodo)} disabled={indo === p.periodo} title="Apagar a confirmação" className="ml-1.5 neu-btn p-1.5 rounded-lg border border-border"><FiTrash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      </PlnBloco>
    </div>
  )
}

function AcessosParte({ api }: { api: (u: string) => Promise<any> }) {
  const [dias, setDias] = useState(30)
  const [a, setA] = useState<Acessos | null>(null)
  const [falha, setFalha] = useState<string | null>(null)
  const [vendo, setVendo] = useState<'quem' | 'nao' | 'chamadas'>('quem')
  useEffect(() => {
    setFalha(null)
    api(`/api/vanguard/acessos?dias=${dias}`).then(setA).catch(e => setFalha((e as Error).message))
  }, [dias, api])
  if (falha) return <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-sm text-red-700 dark:text-red-400">{falha}</p>
  if (!a) return <p className="rounded-xl border border-border bg-card px-4 py-3.5 text-sm text-muted-foreground flex items-center gap-2"><FiLoader className="w-4 h-4 animate-spin" /> Lendo os acessos…</p>
  const maior = Math.max(1, ...a.por_dia.map(d => d.pessoas))
  // PLANOS (21/10/2026): período numa PlnBarra, os 4 números em PlnKpi, o
  // gráfico e a lista em PlnBloco (antes: cartões soltos com mb-3).
  return (
    <div className="flex flex-col gap-4">
      <PlnBarra>
        <PlnBarraRot>Período</PlnBarraRot>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDias(d)} className={`neu-btn px-3 py-1.5 rounded-lg border text-sm ${dias === d ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border'}`}>{d} dias</button>
        ))}
      </PlnBarra>
      <PlnKpis>
        <PlnKpi rotulo="Podem ver (com o app)" valor={num(a.resumo.liberados)} />
        <PlnKpi rotulo={`Abriram em ${a.dias} dias`} valor={num(a.resumo.abriram)} />
        <PlnKpi rotulo="Aberturas" valor={num(a.resumo.aberturas)} />
        <PlnKpi rotulo="Chamadas no WhatsApp" valor={num(a.resumo.whatsapp)} />
      </PlnKpis>
      {a.por_dia.length > 0 && (
        <PlnBloco titulo="Pessoas por dia" nota={`últimos ${a.dias} dias`}>
        <div className="px-5 pt-4 pb-4">
          <div className="flex items-end gap-1 h-28">
            {a.por_dia.map(d => (
              <div key={d.dia} className="flex-1 min-w-[6px] flex flex-col items-center justify-end h-full" title={`${dia(d.dia)}: ${d.pessoas} pessoas, ${d.aberturas} aberturas, ${d.whatsapp} WhatsApp`}>
                <div className="w-full rounded-t bg-primary/70" style={{ height: `${Math.round(d.pessoas / maior * 100)}%` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1.5"><span>{dia(a.por_dia[0].dia)}</span><span>{dia(a.por_dia[a.por_dia.length - 1].dia)}</span></div>
        </div>
        </PlnBloco>
      )}
      <PlnBloco titulo="Quem abriu e quem chamou" nota={`últimos ${a.dias} dias`} extra={
      <div className="flex flex-wrap gap-1.5">
        {([['quem', `Quem abriu (${a.pessoas.length})`], ['nao', `Podem ver e não abriram (${a.nao_abriram.length})`], ['chamadas', `Chamadas no WhatsApp (${a.chamadas.length})`]] as const).map(([k, t]) => (
          <button key={k} onClick={() => setVendo(k)} className={`neu-btn px-3 py-1.5 rounded-lg border text-sm ${vendo === k ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border'}`}>{t}</button>
        ))}
      </div>}>
      <div className="overflow-x-auto">
        {vendo === 'quem' ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground">
              {['Nome', 'Cargo', 'Grupo', 'Distrito', 'Aberturas', 'Dias', 'Grupos vistos', 'WhatsApp', 'Primeira', 'Última'].map(h => <th key={h} className={`${stickyTh} px-3 py-2 whitespace-nowrap`}>{h}</th>)}
            </tr></thead>
            <tbody>{a.pessoas.map(p => (
              <tr key={p.codigo} className="border-t border-border">
                <td className="px-3 py-2">{titulo(p.nome) || p.codigo} <span className="text-xs text-muted-foreground">{p.codigo}</span></td>
                <td className="px-3 py-2">{PAPEL_NOME[p.papel || ''] || ''}</td>
                <td className="px-3 py-2">{titulo(p.grupo)}</td><td className="px-3 py-2">{titulo(p.distrito)}</td>
                <td className="px-3 py-2 tabular-nums text-right">{num(p.aberturas)}</td><td className="px-3 py-2 tabular-nums text-right">{num(p.dias)}</td>
                <td className="px-3 py-2 tabular-nums text-right">{num(p.grupos_vistos)}</td><td className="px-3 py-2 tabular-nums text-right">{num(p.whatsapp)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{dataHora(p.primeira)}</td><td className="px-3 py-2 whitespace-nowrap text-xs">{dataHora(p.ultima)}</td>
              </tr>))}
              {!a.pessoas.length && <tr><td colSpan={10} className="px-3 py-4 text-sm text-muted-foreground">Ninguém abriu a aba neste período.</td></tr>}
            </tbody>
          </table>
        ) : vendo === 'nao' ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground">{['Nome', 'Cargo', 'Grupo', 'Distrito'].map(h => <th key={h} className={`${stickyTh} px-3 py-2`}>{h}</th>)}</tr></thead>
            <tbody>{a.nao_abriram.map(p => (
              <tr key={p.codigo} className="border-t border-border"><td className="px-3 py-2">{titulo(p.nome)} <span className="text-xs text-muted-foreground">{p.codigo}</span></td>
                <td className="px-3 py-2">{PAPEL_NOME[p.papel || ''] || ''}</td><td className="px-3 py-2">{titulo(p.grupo)}</td><td className="px-3 py-2">{titulo(p.distrito)}</td></tr>))}
              {!a.nao_abriram.length && <tr><td colSpan={4} className="px-3 py-4 text-sm text-muted-foreground">Todo mundo que pode ver já abriu (ou a aba está liberada só para testadores).</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground">{['Quando', 'Quem chamou', 'Para quem', 'Vitrine'].map(h => <th key={h} className={`${stickyTh} px-3 py-2`}>{h}</th>)}</tr></thead>
            <tbody>{a.chamadas.map((c, i) => (
              <tr key={i} className="border-t border-border"><td className="px-3 py-2 whitespace-nowrap text-xs">{dataHora(c.em)}</td>
                <td className="px-3 py-2">{titulo(c.quem.nome) || c.quem.codigo}</td>
                <td className="px-3 py-2">{c.para ? `${titulo(c.para.nome) || c.para.codigo}` : ''}{c.para?.grupo ? <span className="text-xs text-muted-foreground"> · {titulo(c.para.grupo)}</span> : null}</td>
                <td className="px-3 py-2 text-xs">{c.vitrine ? vit(c.vitrine) : ''}</td></tr>))}
              {!a.chamadas.length && <tr><td colSpan={4} className="px-3 py-4 text-sm text-muted-foreground">Nenhuma chamada no WhatsApp neste período.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      </PlnBloco>
    </div>
  )
}
